import { useEffect, useState } from "react";
import useSeo from "@/seo/useSeo";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Loader2, Lock, ShieldCheck } from "lucide-react";
import { api, apiError, eur, frDate } from "@/lib/api";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { useI18n } from "@/i18n";

const LOCALES = { fr: "fr-FR", en: "en-GB", de: "de-DE", es: "es-ES", it: "it-IT" };

export default function Booking() {
  const [params] = useSearchParams();
  useSeo("/reservation");
  const { t, tg, lang } = useI18n();
  const locDate = (d) =>
    lang === "fr" ? frDate(d) : new Date(`${d}T12:00:00`).toLocaleDateString(LOCALES[lang], { day: "numeric", month: "long", year: "numeric" });
  const [gites, setGites] = useState([]);
  const [slug, setSlug] = useState(params.get("gite") || "chalet-6");
  const [range, setRange] = useState({ start: params.get("start"), end: params.get("end") });
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", nationality: "",
    line1: "", postal_code: "", city: "", country: "",
    guests: 2, adults: 2, purpose: "", message: "",
  });
  const [occupants, setOccupants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cgvOk, setCgvOk] = useState(false);
  const [sent, setSent] = useState(null);

  const PURPOSES = [
    ["", "—"],
    ["Vacances", t("purpose.holiday")],
    ["Week-end en famille", t("purpose.family")],
    ["Séjour entre amis", t("purpose.friends")],
    ["Déplacement professionnel", t("purpose.work")],
    ["Ski / sports d'hiver", t("purpose.ski")],
    ["Événement familial", t("purpose.event")],
    ["Autre", t("purpose.other")],
  ];

  useEffect(() => {
    api.get("/gites").then((r) => setGites(r.data)).catch(() => {});
  }, []);

  const current = gites.find((g) => g.slug === slug);

  const derivedAdults = Math.min(
    Number(form.guests),
    1 + Array.from({ length: Math.max(0, Number(form.guests) - 1) },
      (_, i) => occupants[i]).filter((o) => !o || !o.minor).length,
  );

  useEffect(() => {
    setQuote(null);
    setQuoteErr("");
    if (!range.start || !range.end) return;
    api
      .get("/quote", { params: { gite_slug: slug, start: range.start, end: range.end, adults: derivedAdults } })
      .then((r) => setQuote(r.data))
      .catch((e) => setQuoteErr(apiError(e.response?.data?.detail)));
  }, [slug, range.start, range.end, derivedAdults]);

  const submit = async (e) => {
    e.preventDefault();
    if (!quote) return toast.error(t("book.errNoDates"));
    if (!cgvOk) return toast.error(t("book.cgvRequired"));
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        gite_slug: slug,
        start_date: range.start,
        end_date: range.end,
        guests: Number(form.guests),
        adults: derivedAdults,
        name: `${form.first_name} ${form.last_name}`.trim(),
        first_name: form.first_name,
        last_name: form.last_name,
        nationality: form.nationality,
        address: {
          line1: form.line1, postal_code: form.postal_code,
          city: form.city, country: form.country,
        },
        occupants: occupants.slice(0, Math.max(0, Number(form.guests) - 1)),
        email: form.email,
        phone: form.phone,
        purpose: form.purpose,
        message: form.message,
        origin_url: window.location.origin,
      });
      setSent(data);
      toast.success(t("book.toastSent"));
      if (data.checkout_url) {
        setTimeout(() => { window.location.href = data.checkout_url; }, 1200);
        return;
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8" data-testid="booking-page">
      {sent && (
        <div className="panel mb-12 p-8" data-testid="booking-sent">
          <h2 className="font-display text-3xl text-bone">{t("book.sentTitle")}</h2>
          <p className="mt-4 max-w-2xl text-sm leading-loose text-bone/75">{t("book.sentText")}</p>
        </div>
      )}
      <p className="eyebrow">{t("book.eyebrow")}</p>
      <h1 className="display-xl mt-6 text-6xl text-bone sm:text-7xl">{t("book.title")}</h1>
      <p className="mt-6 max-w-xl text-sm leading-loose text-bone/70">{t("book.intro")}</p>

      <div className="mt-12 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="mb-6 flex flex-wrap gap-3">
            {gites.map((g) => (
              <button
                key={g.slug}
                type="button"
                disabled={!g.active}
                data-testid={`select-gite-${g.slug}`}
                onClick={() => {
                  setSlug(g.slug);
                  setRange({ start: null, end: null });
                }}
                className={`px-6 py-3 text-[11px] uppercase tracking-[0.2em] ${
                  slug === g.slug ? "bg-brass text-ink"
                    : g.active ? "border border-white/15 text-bone/70"
                      : "cursor-not-allowed border border-white/10 text-bone/52"}`}
                style={{ transition: "background-color .3s ease, color .3s ease" }}
              >
                {g.name}{g.active ? "" : ` · ${t("nav.soon")}`}
              </button>
            ))}
          </div>

          <div className="panel p-6 sm:p-8">
            <AvailabilityCalendar giteSlug={slug} range={range} onSelect={setRange} months={2} />
          </div>

          <form onSubmit={submit} className="panel mt-8 space-y-5 p-6 sm:p-8" data-testid="booking-form">
            <h2 className="font-display text-3xl text-bone">{t("book.details")}</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.lastName")}</span>
                <input required className="field" data-testid="booking-name" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.firstName")}</span>
                <input required className="field" data-testid="booking-firstname" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.nationality")}</span>
                <input required className="field" data-testid="booking-nationality" value={form.nationality}
                  onChange={(e) => setForm({ ...form, nationality: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.addr1")}</span>
                <input required className="field" data-testid="booking-address" value={form.line1}
                  onChange={(e) => setForm({ ...form, line1: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.zip")}</span>
                <input required className="field" data-testid="booking-zip" value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.city")}</span>
                <input required className="field" data-testid="booking-city" value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.country")}</span>
                <input required className="field" data-testid="booking-country" value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.email")}</span>
                <input required type="email" className="field" data-testid="booking-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.phone")}</span>
                <input className="field" data-testid="booking-phone" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.guests")}</span>
                <select className="field" data-testid="booking-guests" value={form.guests}
                  onChange={(e) => {
                    const g = Number(e.target.value);
                    setForm({ ...form, guests: g, adults: Math.min(Number(form.adults), g) || g });
                  }}>
                  {Array.from({ length: current?.capacity || 6 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n} {n > 1 ? t("book.persons") : t("book.person")}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t("book.adults")}</span>
                <p className="field flex items-center" data-testid="booking-adults">{derivedAdults}</p>
                <span className="mt-2 block text-[11px] text-bone/72">{t("book.adultsAuto")}</span>
              </label>
            </div>
            <div className="border-t border-white/10 pt-7">
              <p className="eyebrow">{t("book.occupants")}</p>
              <p className="mt-3 text-xs leading-relaxed text-bone/72">{t("book.occupantsNote")}</p>
              <div className="mt-5 space-y-4" data-testid="booking-occupants">
                {Array.from({ length: Math.max(0, Number(form.guests) - 1) }, (_, i) => {
                  const o = occupants[i] || { first_name: "", last_name: "", minor: false, nationality: "" };
                  const set = (patch) => {
                    const next = [...occupants];
                    next[i] = { ...o, ...patch };
                    setOccupants(next);
                  };
                  return (
                    <div key={i} className="border border-white/10 bg-panel p-4">
                      <p className="mb-3 text-[11px] uppercase tracking-[0.18em] text-brass">
                        {t("book.occupant", { n: i + 2 })}
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <input required className="field" placeholder={t("book.lastName")}
                          data-testid={`occupant-last-${i}`} value={o.last_name}
                          onChange={(e) => set({ last_name: e.target.value })} />
                        <input required className="field" placeholder={t("book.firstName")}
                          data-testid={`occupant-first-${i}`} value={o.first_name}
                          onChange={(e) => set({ first_name: e.target.value })} />
                        <input required className="field" placeholder={t("book.nationality")}
                          data-testid={`occupant-nat-${i}`} value={o.nationality}
                          onChange={(e) => set({ nationality: e.target.value })} />
                        <select className="field" data-testid={`occupant-status-${i}`}
                          value={o.minor ? "minor" : "adult"}
                          onChange={(e) => set({ minor: e.target.value === "minor" })}>
                          <option value="adult">{t("book.adult")}</option>
                          <option value="minor">{t("book.minor")}</option>
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <label className="block text-sm">
              <span className="mb-2 block font-medium text-bone">{t("book.purpose")}</span>
              <select className="field" data-testid="booking-purpose" value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
                {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-2 block font-medium text-bone">{t("book.message")}</span>
              <textarea rows={3} className="field" data-testid="booking-message" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </label>
            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-bone/75">
              <input type="checkbox" required checked={cgvOk} data-testid="booking-cgv-accept"
                onChange={(e) => setCgvOk(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#b99b64]" />
              <span>
                {t("book.cgvAccept")}{" "}
                <a href="/cgv" target="_blank" rel="noreferrer" className="underline hover:text-brass">{t("book.cgvLink")}</a>
                {" · "}
                <a href="/annulation" target="_blank" rel="noreferrer" className="underline hover:text-brass">{t("book.cancelLink")}</a>
              </span>
            </label>
            <button type="submit" disabled={!quote || !cgvOk || loading} className="btn-primary w-full" data-testid="booking-submit">
              {loading ? <><Loader2 size={16} className="animate-spin" /> {t("book.sending")}</> : <><Lock size={15} /> {t("book.payObligation")}</>}
            </button>
            <p className="text-center text-xs leading-relaxed text-bone/76">
              <ShieldCheck size={13} className="mr-1 inline" />
              {t("book.legal")}
            </p>
          </form>
        </div>

        <aside className="lg:col-span-5">
          <div className="panel sticky top-28 p-8" data-testid="booking-summary">
            <h2 className="font-display text-3xl text-bone">{t("book.stay")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{current ? tg(current).name : ""}</p>

            <div className="mt-6 flex items-center gap-3 bg-white/5 p-4 text-sm">
              <CalendarDays size={18} className="text-bone" />
              <span data-testid="summary-dates">
                {range.start ? locDate(range.start) : t("book.arrival")} → {range.end ? locDate(range.end) : t("book.departure")}
              </span>
            </div>

            {quoteErr && <p className="mt-5 bg-destructive/10 p-4 text-sm text-destructive" data-testid="quote-error">{quoteErr}</p>}

            {quote ? (
              <div className="mt-6 space-y-3 text-sm">
                <div className="max-h-52 space-y-2 overflow-auto pr-1">
                  {Object.entries(quote.nightly).map(([d, p]) => (
                    <div key={d} className="flex justify-between text-muted-foreground">
                      <span>{locDate(d)}</span>
                      <span>{eur(p)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between border-t border-white/10 pt-3">
                  <span>{quote.nights} {t("book.nights")}</span><span>{eur(quote.subtotal)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("book.cleaning")}</span><span>{eur(quote.cleaning_fee)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground" data-testid="summary-tourist-tax">
                  <span>
                    {t("book.tax")}
                    <span className="block text-[11px] text-bone/68">
                      {t("book.taxLine", {
                        r: quote.tourist_tax_rate.toFixed(2).replace(".", ","),
                        g: quote.taxable_guests,
                        n: quote.nights,
                      })}
                    </span>
                  </span>
                  <span>{eur(quote.tourist_tax)}</span>
                </div>
                <div className="flex items-baseline justify-between border-t border-white/10 pt-4">
                  <span className="font-medium text-bone">{t("book.total")}</span>
                  <span className="font-display text-3xl text-bone" data-testid="summary-total">{eur(quote.total)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground" data-testid="summary-deposit">
                  <span>{t("book.deposit")}</span><span>{eur(quote.deposit_amount)}</span>
                </div>
                <p className="pt-3 text-[11px] leading-relaxed text-bone/68">
                  {t("book.taxNote", {
                    r: quote.tourist_tax_rate.toFixed(2).replace(".", ","),
                    c: quote.tourist_tax_collector,
                    d: eur(quote.deposit_amount),
                  })}
                  <a href="/cgv" className="underline">{t("book.taxNoteCgv")}</a>).
                </p>
              </div>
            ) : (
              !quoteErr && <p className="mt-6 text-sm text-muted-foreground">{t("book.pickDates")}</p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
